import axios from 'axios';
import axiosInstance from 'config/axiosConfig';
import { Customization } from 'types/Customization';
import { Property } from 'types/Property';
import { create } from 'zustand';

interface SearchAiPluginState {
    customization: Customization;
    setCustomization: (colorField: keyof Customization, value: string) => void;

    createCustomization: () => Promise<Customization>;
    getCustomization: () => Promise<Customization>;
    updateCustomization: () => Promise<Customization>;
    properties: Property[];
    fetchAiSearchProperties: (search: string) => Promise<Property[]>;
}

export const useSearchAiPluginStore = create<SearchAiPluginState>((set, get) => ({
    customization: {
        searchButtonText: 'Search Your Dream Property here..',
        searchButtonColor: '#FF4B4B',
        formTitle: 'Get in Touch About This Listing',
        formDescription: 'Your information will be used solely to respond to your inquiry.',
        themeColor: '#56CCF2',
        id: '',
        widgetId: '',
        buttonSize: 'small',
    },
    properties: [],

    setCustomization: (field: keyof Customization, value: string) =>
        set((state) => ({
            customization: {
                ...state.customization,
                [field]: value,
            },
        })),

    async createCustomization() {
        const { themeColor, searchButtonColor, searchButtonText, formDescription, formTitle, buttonSize } = get().customization;
        try {
            const { data, status } = await axiosInstance.post(`/customizations`, {
                searchButtonText,
                searchButtonColor,
                formTitle,
                formDescription,
                themeColor,
                buttonSize,
            });
            if (status === 201) {
                set({ customization: data.data });
            }
            return data.data;
        } catch (error) {
            if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
            throw new Error(error as string);
        }
    },
    async getCustomization() {
        try {
            const { data, status } = await axiosInstance.get(`/customizations/private`);
            if (status === 200 && Object.keys(data.data).length > 0) {
                set({ customization: data.data });
            }
            return data.data;
        } catch (error) {
            if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
            throw new Error(error as string);
        }
    },
    async updateCustomization() {
        const { themeColor, searchButtonColor, searchButtonText, formDescription, formTitle, buttonSize } = get().customization;
        try {
            const { data } = await axiosInstance.patch(`/customizations`, {
                searchButtonText,
                searchButtonColor,
                formTitle,
                formDescription,
                themeColor,
                buttonSize,
            });
            set({ customization: data.data });
            return data.data;
        } catch (error) {
            if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
            throw new Error(error as string);
        }
    },
    async fetchAiSearchProperties(search: string) {
        const { data } = await axiosInstance.post('/customizations/search', { search });
        set({ properties: data.data });
        return data.data;
    },
}));
